import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const manifestoPoints = [
  {
    principle: "Authority is engineered, not inherited.",
    explanation: "Systems replace personality. Governance replaces charisma. Structure replaces heroics."
  },
  {
    principle: "Certification is not education.",
    explanation: "We do not teach. We certify operating standards that can be audited, enforced, and revoked."
  },
  {
    principle: "Licensing is not consulting.",
    explanation: "Consulting ends when the engagement ends. Licensing creates ongoing governance and revenue."
  },
  {
    principle: "Enforcement is not optional.",
    explanation: "Authority without enforcement is theater. We revoke access. We log violations. We act."
  },
  {
    principle: "Founders are replaceable by design.",
    explanation: "If the business cannot operate without the founder, it is not a business. It is a job."
  },
  {
    principle: "Buyers acquire systems, not personalities.",
    explanation: "The infrastructure must survive founder absence. That is the only definition of enterprise value."
  },
];

const whatWeReject = [
  "Personal branding as strategy",
  "Content as product",
  "Courses as business models",
  "Personality-driven scale",
  "Unenforceable credentials",
  "Open enrollment",
];

const whatWeEngineer = [
  "Codified authority systems",
  "Licensed operating standards",
  "Auditable certification",
  "Revocable access",
  "Governance infrastructure",
  "Buyer-ready architecture",
];

const ManifestoPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="manifesto-page">
      <SEOHead 
        title="Manifesto - OnPoint Authority Systems"
        description="Authority is engineered, not marketed. The founding principles of OnPoint Authority Systems."
        url="https://onpointauthority.com/manifesto"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-24 px-6 md:px-12" data-testid="manifesto-hero">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              Founding Principles
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Authority Is<br />
              <span className="text-primary">Engineered.</span>
            </h1>
            <p className="text-xl font-serif text-muted-foreground">
              Not marketed. Not performed. Not inherited.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Opening Statement */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <p className="text-lg md:text-xl font-light leading-relaxed text-foreground mb-8">
              Most businesses are built on the assumption that the founder is irreplaceable. 
              The brand, the relationships, the decisions — all flow through one person.
            </p>
            <p className="text-lg md:text-xl font-light leading-relaxed text-foreground mb-8">
              This is not a business. This is a dependency.
            </p>
            <p className="text-lg md:text-xl font-light leading-relaxed text-primary">
              OnPoint Authority Systems exists to replace that dependency with infrastructure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="principles-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16 text-center"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              What We Believe
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Core <span className="text-primary">Principles</span>
            </h2>
          </motion.div>

          <div className="space-y-8">
            {manifestoPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="border-l-2 border-primary pl-8 py-4"
              >
                <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-3">
                  {point.principle}
                </h3>
                <p className="text-muted-foreground text-lg">
                  {point.explanation}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Reject vs What We Engineer */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="contrast-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* What We Reject */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-red-500/5 border border-red-500/20 p-8">
                <p className="text-xs font-medium tracking-[0.2em] uppercase text-red-400 mb-6">
                  What We Reject
                </p>
                <ul className="space-y-4">
                  {whatWeReject.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-foreground">
                      <div className="w-2 h-0.5 bg-red-400" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* What We Engineer */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-primary/5 border border-primary/20 p-8">
                <p className="text-xs font-medium tracking-[0.2em] uppercase text-primary mb-6">
                  What We Engineer
                </p>
                <ul className="space-y-4">
                  {whatWeEngineer.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-foreground">
                      <div className="w-2 h-0.5 bg-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Standard */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="standard-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              The Standard
            </p>
            <blockquote className="font-serif text-3xl md:text-4xl font-light text-foreground leading-relaxed mb-8">
              "If the founder cannot leave,<br />
              the business cannot be sold.<br />
              <span className="text-primary">We build what founders cannot take with them.</span>"
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* The Book */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="book-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-primary mb-4">
              The Doctrine
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-6">
              OnPoint Authority
            </h2>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              The complete framework for engineering founder-independent businesses. 
              From authority architecture to enforcement systems — the institutional 
              playbook for operators preparing to scale, delegate, or exit.
            </p>
            <p className="text-sm text-muted-foreground italic">
              By Tessa Shepard
            </p>
          </motion.div>
        </div>
      </section>

      {/* Not For Everyone */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="not-for-everyone">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-4xl font-light text-foreground">
              This Is Not <span className="text-muted-foreground">For Everyone</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-card/5 border border-white/10 p-8"
          >
            <p className="text-lg text-foreground mb-6 leading-relaxed">
              We do not work with founders who want to remain indispensable.
            </p>
            <p className="text-lg text-foreground mb-6 leading-relaxed">
              We do not build systems that protect egos.
            </p>
            <p className="text-lg text-foreground mb-6 leading-relaxed">
              We do not issue credentials that cannot be revoked.
            </p>
            <p className="text-lg text-primary leading-relaxed">
              We work with operators who want to build infrastructure that outlasts them.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="manifesto-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              If This Resonates
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-10">
              Begin with the<br />
              <span className="text-primary">System</span>
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/systems">
                <Button
                  variant="outline"
                  className="border-white/20 hover:bg-white/5 uppercase tracking-[0.15em] text-xs font-bold px-8 py-6 rounded-none"
                  data-testid="manifesto-systems-btn"
                >
                  View the System
                </Button>
              </Link>
              <Link to="/authority-review">
                <Button
                  className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-8 py-6 rounded-none gold-glow"
                  data-testid="manifesto-cta-btn"
                >
                  Request Authority Review
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ManifestoPage;
